import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBrand } from "@/lib/brand";
import { api } from "@/lib/api";
import { Bell, Menu, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/professionals", label: "Find Experts", testid: "nav-browse" },
  { to: "/match", label: "AI Match", testid: "nav-match" },
  { to: "/pricing", label: "Pricing", testid: "nav-pricing" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/faq", label: "FAQs", testid: "nav-faq" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let timer;
    if (user?.role === "admin") {
      const fetchCount = async () => {
        try {
          const { data } = await api.get("/admin/pending-count");
          setPendingCount(data.count || 0);
        } catch { /* ignore */ }
      };
      fetchCount();
      timer = setInterval(fetchCount, 30000);
    }
    return () => timer && clearInterval(timer);
  }, [user?.role]);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const isActive = (to) => location.pathname === to;

  return (
    <header className="sticky top-0 z-40 border-b border-[#142542] backdrop-blur-xl bg-[#05070C]/85">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.name} className="h-10 md:h-11 w-auto" />
          ) : (
            <div className="font-display text-xl tracking-tight text-[#E8ECF3]">
              {brand.name}
            </div>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to}
                  className={`transition-colors ${isActive(n.to) ? "text-[#2DD4BF]" : "text-[#94A3B8] hover:text-[#E8ECF3]"}`}
                  data-testid={n.testid}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user?.role === "admin" && (
            <button
              onClick={() => navigate("/admin")}
              className="relative rounded-full p-2 hover:bg-[#0F1B33] transition-colors border border-[#142542]"
              data-testid="admin-bell"
              title="Admin notifications"
            >
              <Bell className="h-4 w-4 text-[#2DD4BF]" />
              {pendingCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#2DD4BF] text-[#05070C] text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-semibold">
                  {pendingCount}
                </span>
              )}
            </button>
          )}
          {!user ? (
            <>
              <Link to="/login">
                <button className="text-sm text-[#94A3B8] hover:text-[#E8ECF3] px-4 py-2" data-testid="header-login-btn">
                  Login
                </button>
              </Link>
              <Link to="/register">
                <button className="pill-solid text-sm px-5 py-2" data-testid="header-signup-btn">
                  Sign Up
                </button>
              </Link>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-[#142542] px-3 py-1.5 hover:bg-[#0F1B33] transition-colors">
                  <div className="w-7 h-7 rounded-full bg-[#2DD4BF]/20 border border-[#2DD4BF]/40 flex items-center justify-center text-[#2DD4BF] text-xs font-semibold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="text-sm text-[#E8ECF3]">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#0F1B33] border-[#142542] text-[#E8ECF3]">
                <DropdownMenuLabel>
                  <div className="text-xs uppercase tracking-widest text-[#94A3B8]">{user.role}</div>
                  <div className="font-medium truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#142542]" />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Dashboard</DropdownMenuItem>
                {user.role === "professional" && (
                  <DropdownMenuItem onClick={() => navigate("/profile/edit")} data-testid="menu-edit-profile">Edit Profile</DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Admin Panel</DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-[#142542]" />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <button className="md:hidden text-[#E8ECF3]" onClick={() => setMenuOpen((v) => !v)} data-testid="mobile-menu-toggle">
          {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-[#142542] bg-[#0A1223] px-6 py-4 flex flex-col gap-4">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="text-[#E8ECF3]">{n.label}</Link>
          ))}
          {!user ? (
            <>
              <Link to="/login" className="text-[#2DD4BF]">Login</Link>
              <Link to="/register" className="text-[#2DD4BF]">Sign Up</Link>
            </>
          ) : (
            <>
              <Link to="/dashboard" className="text-[#E8ECF3]">Dashboard</Link>
              {user.role === "admin" && <Link to="/admin" className="text-[#2DD4BF]">Admin</Link>}
              <button onClick={handleLogout} className="text-left text-[#94A3B8]">Log out</button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
